import {
    createRootRoute,
    Outlet,
    useLocation,
    useNavigate,
} from "@tanstack/react-router";
import { TanStackRouterDevtools } from "@tanstack/router-devtools";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import AuthProvider from "@/lib/auth/AuthProvider";
import { HeroUIProvider, ToastProvider } from "@heroui/react";
import FeatureFlagProvider from "@/lib/featureFlags/FeatureFlagProvider";
import { LayoutProvider } from "@/lib/layout/LayoutProvider";
import { RequestProvider } from "@/lib/request/RequestProvider";
import ConfirmProvider from "@/lib/confirm/ConfirmProvider";
import { useEffect, useRef } from "react";
import { isLocalhost } from "@/lib/utils/env";
import useIsMobile from "@/lib/utils/useIsMobile";
import InstallPrompt from "@/components/utility/InstallPrompt";
import { datadogRum } from "@datadog/browser-rum";
import SessionPreferencesProvider from "@/lib/sessionPreferences/SessionPreferencesProvider";
import { TourProvider } from "@/lib/tour/TourContext";
import { I18NProvider } from "@/lib/i18n/useI18n";

const RootComponent = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { isMobile } = useIsMobile();

    const initialized = useRef(false);

    useEffect(() => {
        if (isMobile) {
            localStorage.setItem("oldNTime", "false");
            datadogRum.setUserProperty("oldNtime", "false");
            return;
        }

        if (localStorage.getItem("hasForcedNewNT") !== "true") {
            localStorage.setItem("hasForcedNewNT", "true");
            localStorage.setItem("oldNTime", "false");
            datadogRum.setUserProperty("oldNtime", "false");
            window.location.reload();
            return;
        }

        if (localStorage.getItem("oldNTime") == null) {
            localStorage.setItem("oldNTime", "false");
            datadogRum.setUserProperty("oldNtime", "true");
        }
    }, [isMobile]);

    useEffect(() => {
        if (
            localStorage.getItem("oldNTime") === "true" &&
            !initialized.current &&
            !isMobile
        ) {
            initialized.current = true;
            navigate({
                to: "/ntimev1/$",
                params: {
                    _splat:
                        location.pathname
                            ?.replace("/ntimev1/", "")
                            ?.replace("/ntimev1", "")
                            .replace("/fields/", "/field/") ?? "",
                },
                search: location.search,
            });
        }
        initialized.current = true;
    }, [navigate, location.pathname, isMobile, location.search]);

    return (
        <SessionPreferencesProvider>
            <HeroUIProvider>
                <ToastProvider placement="bottom-left" />
                <RequestProvider>
                    <ConfirmProvider>
                        <AuthProvider>
                            <I18NProvider>
                                <FeatureFlagProvider>
                                    <LayoutProvider>
                                        <TourProvider>
                                            <Outlet />
                                        </TourProvider>
                                    </LayoutProvider>
                                </FeatureFlagProvider>
                            </I18NProvider>
                        </AuthProvider>
                    </ConfirmProvider>
                    {isLocalhost() && (
                        <>
                            <TanStackRouterDevtools />
                            <ReactQueryDevtools />
                        </>
                    )}
                    <InstallPrompt />
                </RequestProvider>
            </HeroUIProvider>
        </SessionPreferencesProvider>
    );
};

export const Route = createRootRoute({
    component: RootComponent,
});
